import React from 'react';
import {useLocation} from 'react-router-dom';
import {FormattedMessage} from 'react-intl';
import ServiceList from '../../components/service-list/service-list';
import Skeleton from '../../components/Skeleton';
import TopProjects from './top-projects';
import MainTitleBg from '../../assets/main-title-bg.svg';
import {Helmet} from 'react-helmet-async';
import searchFilter from '../../searchFilter';
import {ErrorBoundary} from 'react-error-boundary';
import CtaSellOnline from '../../components/cta-sell-online/cta-sell-online';
import HomeFaq from './faq';
import CtaSocials from '../../components/cta-socials/cta-socials';
import CtaSupport from '../../components/cta-support/cta-support';
import Trends from './trends';
import HomeVibe from './vibe';
import ErrorFallback from '../../components/ErrorFallback';
import {FetchKeyStateContext} from '../../components/FetchKey';
import {graphql, useLazyLoadQuery} from 'react-relay/hooks';
import HomeCareers from './careers';
import useQuery from '../../useQuery';
import {isTrue} from '../../utility';


const RAZZLE_APP_ORIGIN = process.env.RAZZLE_APP_ORIGIN;

const hlmtImage = `${RAZZLE_APP_ORIGIN}/homepage-social-card.jpg`;
const hlmtSiteName = 'Ottry';
const hlmtUrl = RAZZLE_APP_ORIGIN;

const Home = () => {
    const location = useLocation();
    const {more} = useQuery();
    const fetchKeyState = React.useContext(FetchKeyStateContext);
    const {viewer} = useLazyLoadQuery(
        graphql`
            query homePagesServicesByViewerQuery($first: Int!, $after: String) {
                viewer {
                    targetServices(first: $first, after: $after) @connection(key: "home_targetServices") {
                        edges {
                            node {
                                id
                                slug
                                name
                                nameUk
                                warp
                                target
                                description
                                pin
                            }
                        }
                    }
                }
            }
        `,
        {first: 8},
        {fetchKey: fetchKeyState}
    );
    const pinnedEvents = viewer.targetServices.edges.filter((e) => Number.isInteger(e.node.pin)).map(e => e.node);
    const pinnedEventsQuantity = pinnedEvents.length;
    return (
        <>
            <Helmet>
                <meta property='og:image' content={hlmtImage}/>
                <meta property='og:site_name' content={hlmtSiteName}/>
                <meta property='og:url' content={hlmtUrl}/>
            </Helmet>
            <div className='view'>
                <div className='block padding-top-2rem mw768-padding-top-6rem main-title--block'>
                    <TopProjects {...{services: pinnedEvents}}/>
                </div>
            </div>
            <React.Suspense fallback={
                <div className='padding-top-2dot5rem mw768-padding-top-4rem'>
                    <div className='view'>
                        <div className='block'>
                            <Skeleton/>
                        </div>
                    </div>
                </div>
            }>
                <div className='position-relative'>
                    <ErrorBoundary FallbackComponent={() => null}>
                        <div className='padding-top-2dot5rem mw768-padding-top-4rem'>
                            <div className='view'>
                                <div className='block'>
                                    <Trends/>
                                </div>
                            </div>
                        </div>
                    </ErrorBoundary>
                    {/* TODO: transfer to background after svg processing fix */}
                    <div className='main-title--bg'>
                        <MainTitleBg />
                    </div>
                </div>
                <div className='background-color-gray-50 padding-top-4rem mw768-padding-top-6rem'>
                    <div className='view'>
                        <div className='block'>
                            <div className='text-sm mw768-text-md semibold color-blue-gray-500'><FormattedMessage defaultMessage='Events | Ottry Tickets'/></div>
                            <div className='margin-top-0dot5rem display-sm mw768-display-lg semibold color-gray-900'><FormattedMessage defaultMessage='Upcoming events'/></div>
                            <div className='margin-top-1dot5rem-2rem'>
                                <ErrorBoundary {...{FallbackComponent: ErrorFallback}}>
                                    <ServiceList {...{redirectOnAction: `events${searchFilter(location.search, {fromMore: true})}`, startEventIndex: pinnedEventsQuantity}}/>
                                </ErrorBoundary>
                            </div>
                        </div>
                    </div>
                </div>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <HomeVibe/>
                </ErrorBoundary>
            </React.Suspense>
            <div className='background-color-gray-50'>
                <div className='view'>
                    <div className='block'>
                        <div className='padding-top-3rem mw768-padding-top-4rem padding-bottom-1rem mw768-padding-bottom-4rem'>
                            <CtaSocials/>
                        </div>
                    </div>
                </div>
            </div>
            <HomeFaq/>
            <div className='view'>
                <div className='block'>
                    <div className='padding-top-4rem mw768-padding-top-6rem padding-bottom-4rem mw768-padding-bottom-6rem'>
                        <CtaSupport/>
                    </div>
                </div>
            </div>
            <div className='background-color-gray-50'>
                <div className='view overflow-hidden'>
                    <div className='block'>
                        <div className='padding-top-4rem mw768-padding-top-6rem padding-bottom-4rem mw768-padding-bottom-6rem'>
                            <HomeCareers/>
                        </div>
                    </div>
                </div>
            </div>
            <div className='gradient-grey-900-600-45deg-gradient-grey-800-700-26dot5deg'>
                <CtaSellOnline/>
            </div>
        </>
    );
};

export default React.memo(Home);